import {
  Anchor,
  Button,
  Chip,
  Combobox,
  Divider,
  Input,
  InputPlaceholder,
  InputWrapper,
  List,
  Menu,
  Modal,
  MultiSelect,
  NumberInput,
  Pagination,
  Paper,
  PillsInput,
  SegmentedControl,
  Select,
  Skeleton,
  Switch,
  Table,
  Tabs,
  TagsInput,
  createTheme,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { palette } from 'ui-lib/src/lib/color/palette';
import { palettenext } from 'ui-lib/src/lib/color/palette-next';
import { tokens } from 'ui-lib/src/lib/css/tokens';

import {
  buttonOverrides,
  chipIconWrapperStyles,
  chipLabelStyles,
  comboboxOptionStyles,
  datePickerDayStyles,
  inputStyles,
  listRoot,
  menuItemStyles,
  pillsInputStyles,
  segmentedControlLabelStyles,
  selectOptionStyles,
  skeletonRoot,
  tabListStyles,
  tabStyles,
} from './components.css';

const inputScale = tokens.component.input;

type InputScaleSize = keyof typeof inputScale;

/**
 * Resolves a Mantine `size` prop to the shared input scale. An undefined size
 * means "no explicit size" and resolves to `lg`, the webapp default.
 */
function getInputScale(size: unknown): (typeof inputScale)[InputScaleSize] | undefined {
  const key = size ?? 'lg';
  return typeof key === 'string' && key in inputScale
    ? inputScale[key as InputScaleSize]
    : undefined;
}

export const components = createTheme({
  components: {
    Combobox: Combobox.extend({
      defaultProps: {
        shadow: 'md',
      },
      styles: (theme) => ({
        dropdown: {
          borderRadius: theme.radius.md,
        },
      }),
      classNames: {
        option: comboboxOptionStyles,
      },
    }),
    Divider: Divider.extend({
      defaultProps: {
        color: 'gray.1',
      },
    }),
    // Border color, interaction states and geometry for TimeInput,
    // DatePickerInput, NumberInput, Select and every other Input-based
    // control come from the shared `Input` entry below — only behaviour
    // unique to a component is declared on its own entry.
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        popoverProps: {
          radius: 'md',
          shadow: 'md',
        },
      },
      classNames: {
        day: datePickerDayStyles,
      },
    }),

    NumberInput: NumberInput.extend({
      defaultProps: {
        // The increment/decrement controls (`--ni-chevron-size`,
        // `--ni-right-section-width`) scale from NumberInput's own size prop,
        // which the shared Input entry cannot reach — keep it in step with
        // the lg default of the input scale.
        size: 'lg',
      },
    }),

    // The pills-based inputs resolve theme entries under their own names, so
    // the shared vertical-centering class is attached to each of them.
    PillsInput: PillsInput.extend({
      classNames: {
        input: pillsInputStyles,
      },
    }),

    MultiSelect: MultiSelect.extend({
      classNames: {
        input: pillsInputStyles,
      },
    }),

    TagsInput: TagsInput.extend({
      classNames: {
        input: pillsInputStyles,
      },
    }),

    Table: Table.extend({
      defaultProps: {
        verticalSpacing: 'sm',
        horizontalSpacing: 'lg',
        withRowBorders: true,
      },
      styles: (theme) => ({
        table: {
          backgroundColor: theme.white,
          borderRadius: theme.radius.md,
          overflow: 'hidden',
          borderCollapse: 'collapse',
        },
        th: {
          fontWeight: 600,
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
        },
        td: {
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
        },
      }),
    }),

    Tabs: Tabs.extend({
      classNames: {
        tab: tabStyles,
        list: tabListStyles,
      },
      styles: (theme) => ({
        list: {
          '--tabs-list-border-size': '1px',
          '--tab-border-color': theme.colors.gray[2],
        },
      }),
    }),

    Pagination: Pagination.extend({
      defaultProps: {
        color: 'brand.4',
        radius: 'md',
      },
    }),

    Button: Button.extend({
      defaultProps: {
        radius: 'md',
        color: 'dark',
        size: 'lg',
      },
      // `md` is the design system's 40px button (16px x 12px padding,
      // Label/Small 14px text); Mantine's own md is 42px with 16px text.
      vars: (theme, props) => ({
        root:
          props.size === 'md'
            ? {
                '--button-height': '40px',
                '--button-padding-x': theme.spacing.md,
                '--button-fz': theme.fontSizes.sm,
              }
            : {},
      }),
      classNames: {
        root: buttonOverrides,
      },
    }),

    // Links read in the body ink rather than Mantine's primary-colour blue;
    // the theme sets no primaryColor, so the fallback would be Mantine's blue.
    Anchor: Anchor.extend({
      defaultProps: {
        c: 'content-primary.0',
      },
    }),

    List: List.extend({
      // `sm` list copy sits on a 20px line box (Paragraph/Small 14px/20px);
      // other sizes keep Mantine's line-height scale.
      vars: (_theme, props) => ({
        root: {
          '--list-lh': props.size === 'sm' ? '20px' : undefined,
        },
      }),
      classNames: {
        root: listRoot,
      },
    }),

    Chip: Chip.extend({
      defaultProps: {
        variant: 'outline',
        radius: 'xl',
      },
      classNames: {
        label: chipLabelStyles,
        iconWrapper: chipIconWrapperStyles,
      },
    }),

    Input: Input.extend({
      // Geometry for all Input-based controls (TextInput, Select, Textarea,
      // PillsInput, date/time inputs, …): Mantine resolves the `Input` theme
      // entry for every derived component, so this is the single place that
      // owns the input size scale. `size`/`variant`/`multiline` arrive here
      // from the outer component, so an undefined size means "no explicit
      // size" for every consumer — default to lg per the input contract.
      vars: (theme, props) => {
        // Custom (numeric) sizes are not in the scale and fall back to
        // Mantine's own resolution.
        const scale = getInputScale(props.size);
        return {
          wrapper: {
            // Semantic 8px radius unless the consumer passes one explicitly
            // (an undefined var is dropped, keeping Mantine's resolution).
            '--input-radius': props.radius === undefined ? theme.radius.large : undefined,
            '--input-height': scale?.height,
            '--input-fz': scale?.fontSize,
            // Vertical padding only applies to multiline controls (Textarea,
            // PillsInput). Skipped for unstyled, where Mantine's CSS zeroes
            // the consumed var and an inline value would beat it.
            '--input-padding-y':
              scale && props.multiline && props.variant !== 'unstyled' ? scale.paddingY : undefined,
          },
        };
      },
      styles: (theme, props) => ({
        wrapper: {
          // Horizontal padding lives here rather than in `vars` because
          // `--input-padding` is not part of Input's typed vars API. Only
          // the *definition* var is overridden; the consumed
          // `--input-padding-inline-*` vars still come from Mantine's CSS,
          // which replaces them for the unstyled variant (zeroed) and for
          // inputs with sections (section width reserves the padding), so
          // both keep working. Section width must only be customized via
          // `leftSectionWidth`/`rightSectionWidth`.
          '--input-padding': getInputScale(props.size)?.paddingX,
        },
        input: {
          borderColor: 'var(--mantine-color-border-secondary-filled)',
          color: theme.black,
        },
      }),
      classNames: {
        input: inputStyles,
      },
    }),

    InputWrapper: InputWrapper.extend({
      styles: (theme) => ({
        label: {
          fontSize: theme.fontSizes.sm,
          fontWeight: 600,
          letterSpacing: '0.00938em',
          marginBottom: 6,
          color: theme.black,
        },

        description: {
          fontWeight: 600,
          fontSize: theme.fontSizes.sm,
          color: theme.colors.gray[6],
          marginTop: 4,
        },

        error: {
          fontWeight: 600,
          fontSize: theme.fontSizes.sm,
          marginTop: 4,
          color: palette.red[100],
        },
      }),
    }),

    Modal: Modal.extend({
      defaultProps: {
        centered: true,
        radius: 'lg',
        withCloseButton: false,
        padding: 0,
      },
      styles: () => ({
        header: {
          padding: '1rem',
          borderBottom: `1px solid ${palette.neutral[10]}`,
        },
      }),
    }),

    Menu: Menu.extend({
      classNames: {
        item: menuItemStyles,
      },
    }),

    Paper: Paper.extend({
      defaultProps: {
        shadow: 'md',
        radius: 'md',
      },
    }),

    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        size: 'lg',
        radius: 'large',
      },
      styles: (theme) => ({
        root: {
          height: '3rem',
          backgroundColor: palettenext.neutral[100],
          borderRadius: theme.radius.large,
          flex: 1,
        },
        label: {
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: theme.radius.large,
        },
      }),
      classNames: {
        label: segmentedControlLabelStyles,
      },
    }),

    Select: Select.extend({
      defaultProps: {
        // Note: do NOT set a blanket rightSectionPointerEvents here. Mantine's
        // Select already resolves it per instance ('all' when clearable so the
        // clear button is clickable, 'none' otherwise); a blanket value wins
        // over that and leaves every clearable Select's X dead.
        checkIconPosition: 'right',
        comboboxProps: {
          // Dropdown typography stays on the md scale; option rows remain
          // content-driven and do not inherit the trigger's height.
          size: 'md',
        },
      },
      classNames: {
        option: selectOptionStyles,
      },
    }),

    Skeleton: Skeleton.extend({
      classNames: {
        root: skeletonRoot,
      },
    }),

    Switch: Switch.extend({
      defaultProps: {
        color: 'brand.4',
      },
      styles: () => ({
        track: {
          cursor: 'pointer',
        },
      }),
    }),

    InputPlaceholder: InputPlaceholder.extend({
      styles: {
        placeholder: {
          color: 'var(--mantine-color-placeholder)',
        },
      },
    }),
  },
});
